import { useEffect, useState } from "react";
import { Column } from "../Column/Column";
import { DndContext, DragEndEvent } from "@dnd-kit/core";
import type { IDeclarantsWithApplicationsResponse } from "../../types";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import Header from "../Header/Header";
import {
  changeApplicationsQueue,
  getDeclarantsWithApplications,
} from "../../redux/UserSlice";
import s from "./Queue.module.scss";
import { notification } from "antd";
import { CheckCircleOutlined, WarningOutlined } from "@ant-design/icons";
import Footer from "../Footer/Footer";
import MobileHeader from "../MobileHeader/MobileHeader";
import useWindowDimensions from "../WindowDimensions/WindowDimensions";
import { useTranslation } from "react-i18next";

interface TTarget {
  draggedTaskId: string;
  destinationColumnId: string;
  sourceColumn: number;
}
export default function Queue() {
  const dispatch: AppDispatch = useDispatch();
  const { width } = useWindowDimensions();
  const { t } = useTranslation();
  const declarants = useSelector(
    (state: RootState) => state.user.declarantsList
  );
  const [columns, setColumns] = useState<
    IDeclarantsWithApplicationsResponse[] | []
  >(declarants);
  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    dispatch(getDeclarantsWithApplications());
  }, [dispatch]);

  const openNotification =
    (pauseOnHover: boolean, message: string, type: "success" | "error") =>
    () => {
      let icon;
      switch (type) {
        case "success":
          icon = <CheckCircleOutlined style={{ color: "green" }} />;
          break;

        case "error":
          icon = <WarningOutlined style={{ color: "red" }} />;
          break;
      }
      api.open({
        message,
        icon,
        showProgress: true,
        pauseOnHover,
      });
    };

  const updateColumns = async ({
    draggedTaskId,
    destinationColumnId,
    sourceColumn,
  }: TTarget) => {
    const res = await dispatch(
      changeApplicationsQueue({
        from_declarant: Number(sourceColumn),
        to_declarant: Number(destinationColumnId),
        applications_id: Number(draggedTaskId),
      })
    );

    if (res.type === "admin/changeApplicationsQueue/fulfilled") {
      dispatch(getDeclarantsWithApplications());

      openNotification(
        true,
        "Вы передали заявление на рассмотрение другому декларанту",
        "success"
      )();
    }
  };

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;

    const draggedTaskId = active.id as string;
    const destinationColumnId = over.id as string;

    // Найти исходную колонку, в которой находится таска
    const sourceColumnIndex = columns?.findIndex((col) =>
      col.applications.some((task) => task.id === draggedTaskId)
    );
    if (sourceColumnIndex === -1) return;
    const sourceColumn = columns[sourceColumnIndex];

    const taskIndex = sourceColumn.applications.findIndex(
      (task) => task.id === draggedTaskId
    );
    const draggedTask = sourceColumn.applications[taskIndex];

    // Если таска уже в нужной колонке – ничего не делаем
    if (sourceColumn.id === destinationColumnId) return;

    // Удалить таску из исходной колонки
    const newSourceTasks = [...sourceColumn.applications];
    newSourceTasks.splice(taskIndex, 1);

    // Найти колонку-получатель
    const destColumnIndex = columns.findIndex(
      (col) => col.id === destinationColumnId
    );
    if (destColumnIndex === -1) return;
    const destColumn = columns[destColumnIndex];
    // Обновить статус таски, если это необходимо
    const updatedTask = { ...draggedTask };
    const newDestTasks = [...destColumn.applications, updatedTask];

    // Обновить состояние колонок
    const newColumns = [...columns];
    newColumns[sourceColumnIndex] = {
      ...sourceColumn,
      applications: newSourceTasks,
    };
    newColumns[destColumnIndex] = {
      ...destColumn,
      applications: newDestTasks,
    };
    setColumns(newColumns);
    updateColumns({
      draggedTaskId,
      destinationColumnId,
      sourceColumn: sourceColumn.id,
    });
  }

  return (
    <div>
      {contextHolder}
      {width > 985 ? <Header /> : <MobileHeader />}
      <div className={s.queue}>
        <h2 className={s.h2}>{t("Управление очередью")}</h2>
        <div className={s.item}>
          <DndContext onDragEnd={handleDragEnd}>
            {columns?.map((column) => (
              <Column
                key={column.id}
                column={column}
                applications={column.applications}
              />
            ))}
          </DndContext>
        </div>
      </div>
      <Footer />
    </div>
  );
}
